import { isFunction } from '../shared/runtime-values.js';
type ElementConstructor<T extends Element> = new (...args: never[]) => T;

export function requireElement<T extends Element>(
  selector: string,
  constructor: ElementConstructor<T>,
  root: ParentNode = document,
): T {
  const element = root.querySelector(selector);
  if (!(element instanceof constructor)) {
    throw new Error(`Required element missing or wrong type: ${selector}`);
  }
  return element;
}

export type ToastKind = 'success' | 'error' | 'warning' | 'info';

export type ToastOptions = {
  kind?: ToastKind;
  duration?: number | null;
  action?: { label: string; onClick: (event: MouseEvent) => void };
};

function createToastIcon(kind: ToastKind): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '2.5');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', kind === 'success' ? 'm5 12 4 4L19 6' : kind === 'info' ? 'M12 16v-4m0-4h.01' : 'M12 8v4m0 4h.01');
  svg.appendChild(path);
  return svg;
}

export function createToast(element: HTMLElement | null, { duration: defaultDuration = 3500 } = {}) {
  const toasts = new Map<string, {
    element: HTMLElement;
    hideTimer: ReturnType<typeof setTimeout> | null;
  }>();

  const dismissByKey = (key: string, immediate = false) => {
    const toast = toasts.get(key);
    if (!toast) return;
    if (toast.hideTimer) clearTimeout(toast.hideTimer);
    toasts.delete(key);
    if (immediate) {
      toast.element.remove();
      element?.classList.toggle('show', toasts.size > 0);
      return;
    }
    toast.element.classList.add('is-leaving');
    setTimeout(() => {
      toast.element.remove();
      element?.classList.toggle('show', toasts.size > 0);
    }, 200);
  };

  return {
    show(message: string, { kind = 'info', duration = undefined, action }: ToastOptions = {}) {
      if (!element) return;
      const key = `${kind}\u0000${message}`;
      const timeout = duration === undefined ? (action ? null : kind === 'error' ? 5500 : defaultDuration) : duration;
      const existing = toasts.get(key);
      if (existing) dismissByKey(key, true);

      element.querySelectorAll<HTMLElement>('.toast[data-kind="error"]').forEach((toast) => {
        toast.setAttribute('role', 'status');
        toast.setAttribute('aria-live', 'off');
      });

      const toast = document.createElement('div');
      toast.className = 'toast mapui:inline-flex mapui:items-center mapui:box-border mapui:w-[100%] mapui:gap-[10px] util:p-[10px_10px_10px_12px] util:[border:1px_solid_var(--border-color)] util:rounded-[12px] mapui:[background:var(--bg-toolbar)] mapui:[box-shadow:0_4px_24px_var(--shadow-color),_0_1px_2px_rgb(0_0_0_/_8%)] mapui:[color:var(--text-primary)] util:text-[14px] util:font-[600] util:leading-[1.4] mapui:[opacity:0] mapui:pointer-events-auto util:[transform:translateY(16px)] util:[transition:opacity_0.2s_ease,_transform_0.2s_ease] util:[&[data-kind=success]_.toast-icon]:[background:#dcfce7] util:[&[data-kind=success]_.toast-icon]:[color:#15803d] util:[&[data-kind=warning]_.toast-icon]:[background:#fef3c7] util:[&[data-kind=warning]_.toast-icon]:[color:#b45309] util:[&[data-kind=error]_.toast-icon]:[background:#fee2e2] util:[&[data-kind=error]_.toast-icon]:[color:#dc2626] util:[&.show]:[opacity:1] util:[&.show]:[transform:translateY(0)] util:[&.is-leaving]:[opacity:0] util:[&.is-leaving]:[transform:translateY(16px)] util:[@media_(prefers-reduced-motion:_reduce)]:[transition:none]';
      toast.dataset.kind = kind;
      toast.setAttribute('role', kind === 'error' ? 'alert' : 'status');
      toast.setAttribute('aria-live', kind === 'error' ? 'assertive' : 'polite');
      toast.setAttribute('aria-atomic', 'true');
      const icon = document.createElement('span');
      icon.className = 'toast-icon mapui:grid util:w-[24px] mapui:h-[24px] mapui:[flex-shrink:0] mapui:[place-items:center] mapui:rounded-[50%] util:[background:#e0e7ff] util:[color:#4338ca] util:[&_svg]:w-[15px] util:[&_svg]:h-[15px]';
      icon.setAttribute('aria-hidden', 'true');
      icon.appendChild(createToastIcon(kind));
      const text = document.createElement('span');
      text.className = 'toast-message mapui:min-w-[0]';
      text.textContent = message;
      const controls = document.createElement('span');
      controls.className = 'toast-controls mapui:inline-flex mapui:[flex-shrink:0] mapui:items-center mapui:gap-[2px] util:[margin-inline-start:auto]';
      if (action) {
        const actionButton = document.createElement('button');
        actionButton.className = 'toast-action util:[border:0] mapui:[background:transparent] mapui:[color:#2563eb] util:font-inherit mapui:[cursor:pointer] mapui:whitespace-nowrap mapui:p-[5px_7px] util:rounded-[7px] util:text-[13px] util:font-[700] util:[@media_(hover:_hover)_and_(pointer:_fine)]:[&:hover]:[background:var(--bg-hover)] util:[&:focus-visible]:[outline:none] util:[&:focus-visible]:[box-shadow:0_0_0_3px_rgb(59_130_246_/_22%)]';
        actionButton.type = 'button';
        actionButton.textContent = action.label;
        actionButton.addEventListener('click', event => { dismissByKey(key); action.onClick(event); });
        controls.appendChild(actionButton);
      }
      const dismissButton = document.createElement('button');
      dismissButton.className = 'toast-dismiss util:[border:0] mapui:[background:transparent] mapui:[color:var(--text-secondary)] util:font-inherit mapui:[cursor:pointer] mapui:w-[28px] mapui:h-[28px] mapui:p-[0] util:rounded-[50%] util:text-[22px] util:font-[400] util:leading-[1] util:[@media_(hover:_hover)_and_(pointer:_fine)]:[&:hover]:[background:var(--bg-hover)] util:[&:focus-visible]:[outline:none] util:[&:focus-visible]:[box-shadow:0_0_0_3px_rgb(59_130_246_/_22%)]';
      dismissButton.type = 'button';
      dismissButton.setAttribute('aria-label', element.dataset.dismissLabel || 'Dismiss');
      dismissButton.textContent = '×';
      dismissButton.addEventListener('click', () => dismissByKey(key));
      controls.appendChild(dismissButton);

      toast.appendChild(icon);
      toast.appendChild(text);
      toast.appendChild(controls);
      element.insertBefore(toast, element.firstChild);
      toasts.set(key, { element: toast, hideTimer: timeout === null ? null : setTimeout(() => dismissByKey(key), timeout) });
      element.classList.add('show');
      while (toasts.size > 3) {
        const oldest = Array.from(toasts.entries()).find(([, candidate]) => candidate.element === element.lastElementChild);
        if (oldest) dismissByKey(oldest[0], true);
      }
      if (isFunction(globalThis.requestAnimationFrame)) globalThis.requestAnimationFrame(() => toast.classList.add('show'));
      else toast.classList.add('show');
    },
    dismiss(message: string, { kind = 'info', immediate = false }: Pick<ToastOptions, 'kind'> & { immediate?: boolean } = {}) {
      dismissByKey(`${kind}\u0000${message}`, immediate);
    }
  };
}

export function selectTextInput(input: HTMLInputElement | HTMLTextAreaElement | null) {
  if (!input) return;
  input.focus();
  input.select();
  if (isFunction(input.setSelectionRange)) {
    input.setSelectionRange(0, input.value.length);
  }
}

type LegacyCopyDocument = {
  execCommand(commandId: 'copy'): boolean;
};

function copySelectedTextForLegacyBrowser(): boolean {
  // SAFETY: The owned application template defines this element and its concrete DOM type.
  return (document as LegacyCopyDocument).execCommand('copy');
}

export async function copyTextWithFallback(text: string, inputForManualSelection: HTMLInputElement | HTMLTextAreaElement | null = null) {
  if (navigator.clipboard) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch (error) {
      if (!inputForManualSelection) throw error;
    }
  }

  const target: HTMLInputElement | HTMLTextAreaElement = inputForManualSelection || document.createElement('textarea');
  const shouldCleanup = target !== inputForManualSelection;

  if (shouldCleanup) {
    target.value = text;
    target.style.position = 'fixed';
    target.style.opacity = '0';
    target.style.fontSize = '16px';
    document.body.appendChild(target);
  }

  selectTextInput(target);
  try {
    const didCopy = copySelectedTextForLegacyBrowser();
    if (!didCopy) {
      throw new Error('execCommand copy returned false');
    }
  } finally {
    if (shouldCleanup) {
      document.body.removeChild(target);
    }
  }
}

const actionFeedbackTimers = new WeakMap<HTMLElement, ReturnType<typeof setTimeout>>();

function setTextNodeData(el: HTMLElement, text: string): void {
  for (let i = 0; i < el.childNodes.length; i++) {
    if (el.childNodes[i].nodeType === Node.TEXT_NODE) {
      // SAFETY: The generic owner stores and returns the value for the same caller-provided type.
      (el.childNodes[i] as Text).data = text;
      return;
    }
  }
  el.textContent = text;
}

export function flashActionButton(button: HTMLElement | null, label: string | null | undefined, { duration = 1500, state = 'success' } = {}) {
  if (!button) return;
  const textEl = button.querySelector<HTMLElement>('.action-btn-text');
  const previousLabel = textEl ? textEl.textContent : '';
  const previousState = button.dataset.feedback || '';
  const existingTimer = actionFeedbackTimers.get(button);
  if (existingTimer) clearTimeout(existingTimer);

  button.dataset.feedback = state;
  button.classList.add('action-btn-feedback');
  if (textEl && label) setTextNodeData(textEl, label);

  const timer = setTimeout(() => {
    if (textEl && label) setTextNodeData(textEl, previousLabel);
    if (previousState) button.dataset.feedback = previousState;
    else delete button.dataset.feedback;
    button.classList.remove('action-btn-feedback');
    actionFeedbackTimers.delete(button);
  }, duration);
  actionFeedbackTimers.set(button, timer);
}
